import { useCallback, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type UrlInspectionResult = {
  url: string;
  verdict: string | null;
  coverageState: string | null;
  robotsTxtState: string | null;
  indexingState: string | null;
  pageFetchState: string | null;
  crawledAs: string | null;
  lastCrawlTime: string | null;
  googleCanonical: string | null;
  userCanonical: string | null;
  sitemaps: string[];
  referringUrls: string[];
  mobileVerdict: string | null;
  richResultsVerdict: string | null;
  error: string | null;
};

export type UrlInspectionReport = {
  status: "ok";
  siteUrl: string;
  targetUrl: string;
  source: "sitemap" | "request";
  truncated: boolean;
  maxUrls: number;
  checkedAt: string;
  summary: { indexed: number; notIndexed: number; other: number; errors: number };
  results: UrlInspectionResult[];
};

type State = {
  status: "idle" | "loading" | "ready" | "error" | "selection_required";
  report: UrlInspectionReport | null;
  candidates: string[];
  error: string | null;
};

export function useSeoUrlInspection() {
  const [state, setState] = useState<State>({
    status: "idle",
    report: null,
    candidates: [],
    error: null,
  });

  const run = useCallback(async (options?: { selectedSiteUrl?: string; urls?: string[] }) => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    const { data, error } = await supabase.functions.invoke("seo-url-inspection", {
      body: {
        ...(options?.selectedSiteUrl ? { selectedSiteUrl: options.selectedSiteUrl } : {}),
        ...(options?.urls?.length ? { urls: options.urls } : {}),
      },
    });

    if (error) {
      let details = error.message;
      let parsed: { status?: string; candidates?: string[]; error?: string } | null = null;
      if (error instanceof FunctionsHttpError) {
        details = await error.context.text();
        try {
          parsed = JSON.parse(details);
        } catch {
          parsed = null;
        }
      }
      if (parsed?.status === "selection_required" && parsed.candidates?.length) {
        setState({
          status: "selection_required",
          report: null,
          candidates: parsed.candidates,
          error: null,
        });
        return;
      }
      console.error("seo-url-inspection failed:", details);
      setState({
        status: "error",
        report: null,
        candidates: [],
        error: parsed?.error ?? details ?? "Could not inspect URLs",
      });
      return;
    }

    setState({
      status: "ready",
      report: data as UrlInspectionReport,
      candidates: [],
      error: null,
    });
  }, []);

  return { ...state, run };
}
